/**
 * Auth Store
 * Zustand store for authentication state
 */

import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import type { User, Tenant, LoginCredentials, RegisterData } from '@/types/auth.js';
import authService from '@/services/auth.js';

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresMfa: boolean;
  mfaSessionToken: string | null;
  error: string | null;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  tenant: null,
  isAuthenticated: false,
  isLoading: true,
  requiresMfa: false,
  mfaSessionToken: null,
  error: null,
};

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        login: async (credentials: LoginCredentials) => {
          set({ isLoading: true, error: null });
          try {
            const response = await authService.login(credentials);

            if (response.requiresMfa) {
              set({
                requiresMfa: true,
                mfaSessionToken: response.mfaSessionToken ?? null,
                isLoading: false,
              });
              return;
            }

            set({
              user: response.user,
              tenant: response.tenant,
              isAuthenticated: true,
              isLoading: false,
              requiresMfa: false,
              mfaSessionToken: null,
            });
          } catch (error) {
            const message =
              error instanceof Error ? error.message : 'Login failed';
            set({ error: message, isLoading: false });
            throw error;
          }
        },

        register: async (data: RegisterData) => {
          set({ isLoading: true, error: null });
          try {
            const response = await authService.register(data);
            set({
              user: response.user,
              tenant: response.tenant,
              isAuthenticated: true,
              isLoading: false,
            });
          } catch (error) {
            const message =
              error instanceof Error ? error.message : 'Registration failed';
            set({ error: message, isLoading: false });
            throw error;
          }
        },

        verifyMfa: async (code: string) => {
          const { mfaSessionToken } = get();
          if (!mfaSessionToken) {
            throw new Error('No MFA session');
          }

          set({ isLoading: true, error: null });
          try {
            const response = await authService.verifyMfa(
              { code },
              mfaSessionToken
            );
            set({
              user: response.user,
              tenant: response.tenant,
              isAuthenticated: true,
              isLoading: false,
              requiresMfa: false,
              mfaSessionToken: null,
            });
          } catch (error) {
            const message =
              error instanceof Error ? error.message : 'MFA verification failed';
            set({ error: message, isLoading: false });
            throw error;
          }
        },

        logout: async () => {
          set({ isLoading: true });
          try {
            await authService.logout();
          } finally {
            set({ ...initialState, isLoading: false });
          }
        },

        fetchUser: async () => {
          if (!authService.isAuthenticated()) {
            set({ isLoading: false });
            return;
          }

          set({ isLoading: true });
          try {
            const user = await authService.getCurrentUser();
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            });
          } catch {
            set({ ...initialState, isLoading: false });
          }
        },

        clearError: () => set({ error: null }),

        setLoading: (loading: boolean) => set({ isLoading: loading }),
      }),
      {
        name: 'auth-storage',
        partialize: (state) => ({
          user: state.user,
          tenant: state.tenant,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    )
  )
);

export default useAuthStore;
